import React, { useMemo, useState } from "react";
import {
    ChevronDown,
    ChevronUp,
    RefreshCw,
    DollarSign,
    TrendingUp,
    TrendingDown,
} from "lucide-react";
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "../components/misc/Card";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/misc/Table";
import SimpleTooltip from "../components/misc/SimpleTooltip";
import {
    ModelUsageData,
    MonthlyOpenAIUsage,
    OpenAIActivityData,
    OpenAIEnvironmentSpend,
    OpenAIMonthlyData,
    OpenAIUsageResponse,
    OpenAIUserData,
} from "./types";

export type UsageVendor = "OpenAI" | "Anthropic";

// What the Environment Spend card says before that vendor's billing call can run.
const ENV_SPEND_NOT_CONFIGURED: Record<UsageVendor, string> = {
    OpenAI:
        "Environment split not configured yet - it lights up when the DEV/QA project keys and the OPENAI_ADMIN_KEY secret land with the next deployment. Until then this dashboard reflects PROD-recorded usage only.",
    Anthropic:
        "Not configured. This card would show what Anthropic billed us over the last 30 days, split by environment (PROD / DEV / QA), from Anthropic's cost report. The figures above already come from the app's own records (every Claude call's tokens priced at list rates), so nothing is missing without it. If we want the billed view: (1) create an organization-level Anthropic API key that is not tied to a workspace (Anthropic accepts an Admin key, an org:admin token, or a personal or service-account key with no workspace; workspace keys are rejected), (2) store it in Secret Manager as ANTHROPIC_ADMIN_API_KEY on the prod api, (3) build the cost-report call in the api, which is a stub today.",
};

const formatYearMonth = (year: number, month: number): string => {
    const date = new Date(year, month - 1);
    return date.toLocaleString("default", { month: "short", year: "numeric" });
};

interface VendorUsageTabProps {
    vendor: UsageVendor;
    usageData: OpenAIUsageResponse | null;
    monthlyUsage: MonthlyOpenAIUsage[];
    envSpend: OpenAIEnvironmentSpend | null;
    loading: boolean;
    isMobile: boolean;
    onRefresh: () => void;
}

const VendorUsageTab = ({
    vendor,
    usageData,
    monthlyUsage,
    envSpend,
    loading,
    isMobile,
    onRefresh,
}: VendorUsageTabProps): JSX.Element => {
    const [expandedActivities, setExpandedActivities] = useState<Set<string>>(
        new Set()
    );
    const [expandedUserIds, setExpandedUserIds] = useState<Set<string>>(
        new Set()
    );

    const toggleActivityExpansion = (activity: string): void => {
        setExpandedActivities((prev) => {
            const next = new Set(prev);
            if (next.has(activity)) next.delete(activity);
            else next.add(activity);
            return next;
        });
    };

    const toggleUserExpansion = (userId: string): void => {
        setExpandedUserIds((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(userId)) {
                newSet.delete(userId);
            } else {
                newSet.add(userId);
            }
            return newSet;
        });
    };

    const chartData = useMemo(() => {
        if (!monthlyUsage || monthlyUsage.length === 0) return [];

        return monthlyUsage
            .sort((a, b) => {
                const dateA = new Date(a.year, a.month - 1);
                const dateB = new Date(b.year, b.month - 1);
                return dateA.getTime() - dateB.getTime();
            })
            .map(item => ({
                month: formatYearMonth(item.year, item.month),
                totalCost: item.totalCost,
            }));
    }, [monthlyUsage]);

    const models = usageData?.models ?? [];
    const modelTotals = models.reduce(
        (total, row) => ({
            calls: total.calls + row.calls,
            totalInputTokens: total.totalInputTokens + row.totalInputTokens,
            totalOutputTokens: total.totalOutputTokens + row.totalOutputTokens,
            totalCost: total.totalCost + row.totalCost,
        }),
        { calls: 0, totalInputTokens: 0, totalOutputTokens: 0, totalCost: 0 },
    );

    return (
        <div className="space-y-6">
          {/* Summary Statistics */}
          {usageData?.summary && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold">
                  Usage Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className={`${isMobile ? "grid grid-cols-2" : "grid grid-cols-3"} gap-4`}
                >
                  <div
                    className="p-4 rounded-lg border
  bg-gradient-to-br from-green-50 to-emerald-50
  dark:from-green-950/20 dark:to-emerald-950/20
  border-green-200 dark:border-green-800"
                  >
                    {isMobile ? (
                      /* ---------- MOBILE LAYOUT ---------- */
                      <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                        <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                          <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
                        </div>

                        <p className="text-xs font-medium text-muted-foreground self-center">
                          Total Cost
                        </p>

                        <p className="col-span-2 text-lg font-bold text-green-700 dark:text-green-400 break-words">
                          $
                          {usageData.summary.totalCost.toFixed(2)}
                        </p>
                      </div>
                    ) : (
                      /* ---------- DESKTOP LAYOUT ---------- */
                      <div className="flex items-start space-x-3">
                        <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                          <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
                        </div>

                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-muted-foreground mb-1">
                            Total Cost
                          </p>

                          <p className="text-2xl font-bold text-green-700 dark:text-green-400 break-words">
                            $
                            {usageData.summary.totalCost.toFixed(
                              2,
                            )}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div
                    className="p-4 rounded-lg border
  bg-gradient-to-br from-blue-50 to-cyan-50
  dark:from-blue-950/20 dark:to-cyan-950/20
  border-blue-200 dark:border-blue-800"
                  >
                    {isMobile ? (
                      /* ---------- MOBILE ---------- */
                      <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                        <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                          <TrendingUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        </div>

                        <p className="text-xs font-medium text-muted-foreground self-center">
                          Input Tokens
                        </p>

                        <p className="col-span-2 text-lg font-bold text-blue-700 dark:text-blue-400 break-words">
                          {usageData.summary.totalInputTokens.toLocaleString()}
                        </p>
                      </div>
                    ) : (
                      /* ---------- DESKTOP ---------- */
                      <div className="flex items-start space-x-3">
                        <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                          <TrendingUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        </div>

                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-muted-foreground mb-1">
                            Input Tokens
                          </p>

                          <p className="text-2xl font-bold text-blue-700 dark:text-blue-400 break-words">
                            {usageData.summary.totalInputTokens.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div
                    className="p-4 rounded-lg border
  bg-gradient-to-br from-purple-50 to-pink-50
  dark:from-purple-950/20 dark:to-pink-950/20
  border-purple-200 dark:border-purple-800"
                  >
                    {isMobile ? (
                      /* ---------- MOBILE ---------- */
                      <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                        <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                          <TrendingDown className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                        </div>

                        <p className="text-xs font-medium text-muted-foreground self-center">
                          Output Tokens
                        </p>

                        <p className="col-span-2 text-lg font-bold text-purple-700 dark:text-purple-400 break-words">
                          {usageData.summary.totalOutputTokens.toLocaleString()}
                        </p>
                      </div>
                    ) : (
                      /* ---------- DESKTOP ---------- */
                      <div className="flex items-start space-x-3">
                        <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                          <TrendingDown className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                        </div>

                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-muted-foreground mb-1">
                            Output Tokens
                          </p>

                          <p className="text-2xl font-bold text-purple-700 dark:text-purple-400 break-words">
                            {usageData.summary.totalOutputTokens.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {models.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Spend by Model</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Model</TableHead>
                      <TableHead className="text-right">Calls</TableHead>
                      <TableHead className="text-right">Input Tokens</TableHead>
                      <TableHead className="text-right">Output Tokens</TableHead>
                      <TableHead className="text-right">Total Cost ($)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {models.map((row: ModelUsageData) => (
                      <TableRow key={row.model}>
                        <TableCell className="font-medium">{row.label}</TableCell>
                        <TableCell className="text-right">
                          {row.calls.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {row.totalInputTokens.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {row.totalOutputTokens.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          ${row.totalCost.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell className="font-semibold">Total</TableCell>
                      <TableCell className="text-right font-semibold">
                        {modelTotals.calls.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {modelTotals.totalInputTokens.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {modelTotals.totalOutputTokens.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        ${modelTotals.totalCost.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Usage Chart */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle>Monthly {vendor} Usage</CardTitle>
              <SimpleTooltip
                message={`Refresh ${vendor} usage data`}
                visible={true}
                className="w-auto"
              >
                <button
                  onClick={onRefresh}
                  disabled={loading}
                  className="p-2 rounded-md hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshCw
                    className={`h-5 w-5 ${loading ? "animate-spin" : ""}`}
                  />
                </button>
              </SimpleTooltip>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="month"
                    label={{
                      value: "Month",
                      position: "insideBottomRight",
                      offset: -5,
                    }}
                  />
                  <YAxis
                    label={{
                      value: "Total Cost ($)",
                      angle: -90,
                      position: "insideLeft",
                    }}
                    domain={[0, "auto"]}
                  />
                  <Tooltip />
                  <Legend verticalAlign="top" height={36} />
                  <Line
                    type="monotone"
                    dataKey="totalCost"
                    name="Total Cost"
                    stroke="#ffc658"
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* MB-1403: environment spend from the vendor's billing (project keys) */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold">
                Environment Spend ({vendor} billing, last{" "}
                {envSpend?.days ?? 30} days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {envSpend?.configured && !envSpend.error ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Environment / Project</TableHead>
                      <TableHead className="text-right">
                        Spend ($)
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {envSpend.projects.map((project) => (
                      <TableRow key={project.projectId}>
                        <TableCell className="font-medium">
                          {project.name}
                        </TableCell>
                        <TableCell className="text-right">
                          ${project.amountUsd.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell className="font-semibold">
                        Total
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        ${envSpend.totalUsd.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              ) : envSpend && !envSpend.configured ? (
                <p className="text-sm text-muted-foreground">
                  {ENV_SPEND_NOT_CONFIGURED[vendor]}
                </p>
              ) : (
                // MB-1403: configured-but-failing, or the request itself
                // failed. Must NOT reuse the "not configured yet" copy —
                // that tells an admin to wait for a rollout that already
                // happened while the real cause is a broken key.
                <p className="text-sm text-red-500">
                  {envSpend?.error ??
                    "Environment spend could not be loaded - the request to the API failed."}
                </p>
              )}
            </CardContent>
          </Card>

          {/* MB-1403: spend by activity */}
          {usageData?.activities &&
            usageData.activities.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Spend by Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[50px]"></TableHead>
                        <TableHead>Activity</TableHead>
                        <TableHead className="text-right">
                          Total Cost ($)
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {usageData.activities.map(
                        (activityRow: OpenAIActivityData) => {
                          const isExpanded = expandedActivities.has(
                            activityRow.activity,
                          );
                          return (
                            <React.Fragment key={activityRow.activity}>
                              <TableRow
                                className="cursor-pointer hover:bg-muted/50"
                                onClick={() =>
                                  toggleActivityExpansion(
                                    activityRow.activity,
                                  )
                                }
                              >
                                <TableCell>
                                  {isExpanded ? (
                                    <ChevronUp className="h-4 w-4" />
                                  ) : (
                                    <ChevronDown className="h-4 w-4" />
                                  )}
                                </TableCell>
                                <TableCell className="font-medium">
                                  {activityRow.activity}
                                </TableCell>
                                <TableCell className="text-right">
                                  ${activityRow.totalCost.toFixed(2)}
                                </TableCell>
                              </TableRow>
                              {isExpanded && (
                                <TableRow>
                                  <TableCell
                                    colSpan={3}
                                    className="p-0"
                                  >
                                    <div className="bg-muted/30 p-4">
                                      <Table>
                                        <TableHeader>
                                          <TableRow>
                                            <TableHead>Month</TableHead>
                                            <TableHead className="text-right">
                                              Input Tokens
                                            </TableHead>
                                            <TableHead className="text-right">
                                              Output Tokens
                                            </TableHead>
                                            <TableHead className="text-right">
                                              Cost ($)
                                            </TableHead>
                                          </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                          {activityRow.monthlyData.map(
                                            (month) => (
                                              <TableRow
                                                key={`${month.year}-${month.month}`}
                                              >
                                                <TableCell>
                                                  {month.year}-
                                                  {String(
                                                    month.month,
                                                  ).padStart(2, "0")}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                  {month.totalInputTokens.toLocaleString()}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                  {month.totalOutputTokens.toLocaleString()}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                  $
                                                  {month.totalCost.toFixed(
                                                    2,
                                                  )}
                                                </TableCell>
                                              </TableRow>
                                            ),
                                          )}
                                        </TableBody>
                                      </Table>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        },
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

          {/* Usage Table */}
          <Card>
            <CardHeader>
              <CardTitle>{vendor} Usage Details</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]"></TableHead>
                    <TableHead>First Name</TableHead>
                    <TableHead>Last Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="text-right">
                      Total Cost ($)
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usageData?.users &&
                  usageData.users.length > 0 ? (
                    usageData.users.map(
                      (user: OpenAIUserData) => {
                        const isExpanded = expandedUserIds.has(
                          user.userId,
                        );

                        return (
                          <React.Fragment key={user.userId}>
                            {/* Main User Row */}
                            <TableRow
                              className="cursor-pointer hover:bg-muted/50"
                              onClick={() =>
                                toggleUserExpansion(user.userId)
                              }
                            >
                              <TableCell>
                                {isExpanded ? (
                                  <ChevronUp className="h-4 w-4" />
                                ) : (
                                  <ChevronDown className="h-4 w-4" />
                                )}
                              </TableCell>
                              <TableCell className="font-medium">
                                {user.firstName || "-"}
                              </TableCell>
                              <TableCell className="font-medium">
                                {user.lastName || "-"}
                              </TableCell>
                              <TableCell>
                                {user.email || "-"}
                                {user.deleted && (
                                  <span className="ml-1 text-xs text-muted-foreground">
                                    (deleted)
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                ${user.totalCost.toFixed(2)}
                              </TableCell>
                            </TableRow>

                            {/* Expanded Monthly Data */}
                            {isExpanded && (
                              <TableRow>
                                <TableCell colSpan={5} className="p-0">
                                  <div className="bg-muted/30 p-4">
                                    <div className="max-h-96 overflow-y-auto">
                                      <Table>
                                        <TableHeader className="sticky top-0 bg-background z-10">
                                          <TableRow>
                                            <TableHead>Month</TableHead>
                                            <TableHead className="text-right">
                                              Input Tokens
                                            </TableHead>
                                            <TableHead className="text-right">
                                              Output Tokens
                                            </TableHead>
                                            <TableHead className="text-right">
                                              Cost ($)
                                            </TableHead>
                                          </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                          {user.monthlyData
                                            .sort(
                                              (
                                                a: OpenAIMonthlyData,
                                                b: OpenAIMonthlyData,
                                              ) => {
                                                const dateA = new Date(
                                                  a.year,
                                                  a.month - 1,
                                                );
                                                const dateB = new Date(
                                                  b.year,
                                                  b.month - 1,
                                                );
                                                return (
                                                  dateB.getTime() -
                                                  dateA.getTime()
                                                );
                                              },
                                            )
                                            .map(
                                              (
                                                monthData: OpenAIMonthlyData,
                                                idx: number,
                                              ) => (
                                                <TableRow
                                                  key={`${monthData.year}-${monthData.month}-${idx}`}
                                                >
                                                  <TableCell>
                                                    {formatYearMonth(
                                                      monthData.year,
                                                      monthData.month,
                                                    )}
                                                  </TableCell>
                                                  <TableCell className="text-right">
                                                    {monthData.totalInputTokens.toLocaleString()}
                                                  </TableCell>
                                                  <TableCell className="text-right">
                                                    {monthData.totalOutputTokens.toLocaleString()}
                                                  </TableCell>
                                                  <TableCell className="text-right">
                                                    $
                                                    {monthData.totalCost.toFixed(
                                                      2,
                                                    )}
                                                  </TableCell>
                                                </TableRow>
                                              ),
                                            )}
                                        </TableBody>
                                      </Table>
                                    </div>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      },
                    )
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center">
                        No {vendor} Usage Data
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
    );
};

export default VendorUsageTab;
